import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button, EmptyState, Skeleton } from '../../ui';
import { Activity, Inbox, Pause, Play } from 'lucide-react';
import {
  RequestStatus,
  RoutedReason,
  type UsageFilter,
  type UsageRecord,
} from '../../gql/types';
import { providerLabel } from '../shared/providerLabels';
import { formatDateTime, formatNumber } from '../../lib/format';
import { UsageRecordDetail } from './UsageRecordDetail';

// The live, filterable usage records feed (US-8.5.1). Records arrive from the cursor-paginated
// `usageRecords` query AND, live, from the `usageRecorded` subscription (prepended). A subtle
// "live" affordance + a pause/resume control let the operator freeze the stream. UsageRecord is
// METADATA-ONLY — we render model/provider/tokens/latency/status, never any prompt/response.

const STATUS_TONE: Record<RequestStatus, { dark: string; light: string }> = {
  [RequestStatus.Completed]: {
    dark: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
    light: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
  [RequestStatus.Failed]: {
    dark: 'border-rose-500/20 bg-rose-500/10 text-rose-300',
    light: 'border-rose-200 bg-rose-50 text-rose-700',
  },
  [RequestStatus.Cancelled]: {
    dark: 'border-white/[0.1] bg-white/[0.04] text-white/60',
    light: 'border-slate-200 bg-slate-100 text-slate-600',
  },
};

const ROUTED_REASON_LABEL: Record<RoutedReason, string> = {
  [RoutedReason.Preferred]: 'Preferred',
  [RoutedReason.Overflow]: 'Overflow',
  [RoutedReason.UnhealthyFailover]: 'Failover',
  [RoutedReason.Override]: 'Override',
  [RoutedReason.RetryFallback]: 'Retry fallback',
};

/**
 * Does a record satisfy the active filter? Used to decide whether a LIVE pushed record belongs in
 * the current view (the server filters the query, but the subscription push must be re-checked
 * client-side against the same filter so the live feed stays consistent with the query result).
 */
export function recordMatchesFilter(record: UsageRecord, filter: UsageFilter): boolean {
  if (filter.provider && record.chosenProvider !== filter.provider) return false;
  if (filter.status && record.status !== filter.status) return false;
  if (filter.canonicalModel && record.canonicalModel !== filter.canonicalModel) return false;
  if (filter.clientId && record.client.id !== filter.clientId) return false;
  if (filter.applicationId && record.application.id !== filter.applicationId) return false;
  if (filter.apiKeyId && record.apiKey.id !== filter.apiKeyId) return false;
  if (filter.from && new Date(record.insertedAt) < new Date(filter.from)) return false;
  if (filter.to && new Date(record.insertedAt) > new Date(filter.to)) return false;
  return true;
}

function StatusPill({ status }: { status: RequestStatus }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const tone = STATUS_TONE[status];
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
        isDark ? tone.dark : tone.light
      }`}
    >
      {status}
    </span>
  );
}

function RecordRow({
  record,
  isNew,
  onSelect,
}: {
  record: UsageRecord;
  isNew: boolean;
  onSelect: (record: UsageRecord) => void;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const totalTokens = record.promptTokens + record.completionTokens + record.reasoningTokens;

  return (
    <tr
      data-testid="usage-row"
      data-new={isNew || undefined}
      role="button"
      tabIndex={0}
      aria-label={`Inspect request ${record.id}`}
      onClick={() => onSelect(record)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(record);
        }
      }}
      className={`cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
        isNew ? (isDark ? 'bg-indigo-500/[0.07]' : 'bg-indigo-50/60') : ''
      } ${
        isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-slate-50'
      }`}
      style={isNew ? { animation: 'fadeInUp 0.4s ease-out' } : undefined}
    >
      <td className="whitespace-nowrap px-4 py-3">
        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {record.canonicalModel}
        </span>
        <span className={`block font-mono text-[11px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          {record.providerModel}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <span className={`text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          {providerLabel(record.chosenProvider)}
        </span>
        <span className={`block text-[11px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          {ROUTED_REASON_LABEL[record.routedReason]}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        <span className={`tabular-nums text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          {formatNumber(totalTokens)}
        </span>
        <span className={`block text-[11px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          {formatNumber(record.promptTokens)} in · {formatNumber(record.completionTokens)} out
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        <span className={`tabular-nums text-sm ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          {formatNumber(record.totalMs)} ms
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <StatusPill status={record.status} />
        {record.errorCode && (
          <span className={`ml-2 font-mono text-[11px] ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
            {record.errorCode}
          </span>
        )}
      </td>
      <td className={`whitespace-nowrap px-4 py-3 text-right text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
        {formatDateTime(record.insertedAt)}
      </td>
    </tr>
  );
}

export interface UsageFeedProps {
  records: UsageRecord[];
  /** Ids of records that arrived live (for the highlight affordance). */
  liveIds: ReadonlySet<string>;
  loading: boolean;
  hasNextPage: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  paused: boolean;
  onTogglePaused: () => void;
  /** Connection status for the live affordance ('open' = streaming). */
  live: boolean;
}

export function UsageFeed({
  records,
  liveIds,
  loading,
  hasNextPage,
  loadingMore,
  onLoadMore,
  paused,
  onTogglePaused,
  live,
}: UsageFeedProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // The detail modal is feed-local state — keeping it here (vs. lifting to the page)
  // means every consumer of UsageFeed gets the click-to-inspect affordance for free
  // without needing to thread props through.
  const [selected, setSelected] = useState<UsageRecord | null>(null);

  return (
    <section
      aria-label="Usage records"
      className={`overflow-hidden rounded-2xl border ${
        isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <header
        className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${
          isDark ? 'border-white/[0.06]' : 'border-slate-100'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <span
            data-testid="live-indicator"
            data-live={live && !paused ? 'on' : 'off'}
            className={`flex items-center gap-1.5 text-xs font-medium ${
              paused
                ? isDark
                  ? 'text-white/40'
                  : 'text-slate-400'
                : live
                  ? isDark
                    ? 'text-emerald-300'
                    : 'text-emerald-600'
                  : isDark
                    ? 'text-amber-300'
                    : 'text-amber-600'
            }`}
          >
            <span
              className={`relative flex h-2 w-2`}
              aria-hidden="true"
            >
              {!paused && live && (
                <span className="absolute inline-flex h-full w-full motion-safe:animate-ping rounded-full bg-emerald-400 opacity-75" />
              )}
              <span
                className={`relative inline-flex h-2 w-2 rounded-full ${
                  paused ? 'bg-slate-400' : live ? 'bg-emerald-500' : 'bg-amber-500'
                }`}
              />
            </span>
            {paused ? 'Live paused' : live ? 'Live' : 'Reconnecting'}
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={onTogglePaused}
          leftIcon={
            paused ? (
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Pause className="h-3.5 w-3.5" aria-hidden="true" />
            )
          }
        >
          {paused ? 'Resume' : 'Pause'}
        </Button>
      </header>

      {loading && records.length === 0 ? (
        <div className="space-y-2 p-4" data-testid="feed-loading">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="block" height={52} />
          ))}
        </div>
      ) : records.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-14 w-14" aria-hidden="true" />}
          title="No usage yet"
          description="Served requests will stream in here. Adjust the filters above to widen the window."
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr
                  className={`text-[11px] font-medium uppercase tracking-wider ${
                    isDark ? 'text-white/30' : 'text-slate-400'
                  }`}
                >
                  <th scope="col" className="px-4 py-2.5">Model</th>
                  <th scope="col" className="px-4 py-2.5">Provider</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Tokens</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Latency</th>
                  <th scope="col" className="px-4 py-2.5">Status</th>
                  <th scope="col" className="px-4 py-2.5 text-right">When</th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-slate-100'}`}
              >
                {records.map((record) => (
                  <RecordRow
                    key={record.id}
                    record={record}
                    isNew={liveIds.has(record.id)}
                    onSelect={setSelected}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {hasNextPage && (
            <div
              className={`flex justify-center border-t p-3 ${
                isDark ? 'border-white/[0.06]' : 'border-slate-100'
              }`}
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={onLoadMore}
                loading={loadingMore}
                leftIcon={<Activity className="h-3.5 w-3.5" aria-hidden="true" />}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      <UsageRecordDetail
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        record={selected}
      />
    </section>
  );
}
